/**
 * apps/web :: lib/profile-api
 *
 * Typed wrappers around the `/api/profile` surface shipped in PR #108
 * (ROADS-290). Field names mirror the backend wire shape verbatim —
 * `@SerialName` on the shared Kotlin `UserProfile` produces snake_case
 * keys, so the TS types keep snake_case too (same pattern as the auth
 * `User` type in `auth/types.ts`). Keeping one shape across the boundary
 * avoids the adapter layer we removed in PR #63.
 *
 * Endpoints:
 *   - GET  /api/profile                    → 200 UserProfile OR 404
 *                                             `{status:"no_profile"}` (we
 *                                             surface null for the 404).
 *   - PUT  /api/profile                    → upsert; server echoes the
 *                                             stored row + derived fields.
 *   - GET  /api/profile/onboarding-status  → lightweight probe the gate
 *                                             uses to decide the redirect.
 *
 * Auth: all three endpoints require the `rb_session` cookie; `apiFetch`
 * always sends credentials so no extra wiring is needed here. 401s bubble
 * up as `ApiError` — the router-level ProtectedRoute catches those by
 * bouncing to `/login`, not this module.
 */
import { ApiError, apiFetch } from "@/lib/api";

/** Wire-format language code. Mirrors `kmp-shared-core LangCode.wire`. */
export type LangCode =
  | "en"
  | "es"
  | "hi"
  | "te"
  | "tl"
  | "ru"
  | "uk"
  | "tr"
  | "zh-Hans";

/**
 * Self-selected platform-interaction flag (ROADS-446 SCHEMA-04).
 *
 * Mirrors `kmp-shared-core/.../shared/profile/Persona` — the wire form is
 * snake_case for every value, identical to the Postgres column literals
 * enforced by the V202605050002__user_profiles_persona Flyway migration.
 *
 * Status-agnostic principle (ROADS-436): persona describes what kind of
 * platform interaction the user wants. It is NOT a citizenship,
 * residency, or visa proxy. Code review will flag if this drifts.
 *
 * The five values are the known set; future additions go through a
 * separate ticket. Defaults at the data-class layer are
 * `resident_studying` so v0 clients that don't surface persona
 * selection (this PR's scope) keep working.
 */
export type Persona =
  | "resident_studying"
  | "tourist_orientation"
  | "outbound_orientation"
  | "study_abroad"
  | "military_stationed";

/**
 * Default persona for any client that doesn't surface a selection step.
 * Mirrors `Persona.DEFAULT` on the Kotlin side. ROADS-446 SCHEMA-04 —
 * the tourist-orientation UI surface (ROADS-443) is what introduces the
 * actual selector; until then this default keeps v0 clients working.
 */
export const DEFAULT_PERSONA: Persona = "resident_studying";

/**
 * USPS two-letter code for an MVP-supported state.
 *
 * Lockstep with `kmp-shared-core/.../shared/profile/UsStateCode.kt`,
 * `packages/analytics/src/events.ts` `StateCode`, and
 * `apps/web/src/lib/states.ts` `UsStateCode`. Wave 9a (ROADS-317)
 * extended the catalog with IL/MA/WA/AZ/GA — the backend rejects
 * unknown codes at /api/profile, so this union must not advertise
 * codes the backend can't accept.
 */
export type UsStateCode =
  | "CA"
  | "TX"
  | "FL"
  | "NY"
  | "NJ"
  | "IL"
  | "MA"
  | "WA"
  | "AZ"
  | "GA";

/**
 * Wire shape of a stored user profile — mirrors the Kotlin
 * `cx.humane.roadbridge.shared.profile.UserProfile` @Serializable data
 * class verbatim, including `@SerialName` snake_case. Any divergence is a
 * bug; validate against the backend JSON in tests rather than mapping here.
 */
export interface UserProfile {
  user_id: string;
  home_country: string;
  native_language: LangCode;
  target_states: UsStateCode[];
  current_focus_state: UsStateCode | null;
  cdl_interest: boolean;
  /**
   * ROADS-446 SCHEMA-04 — self-selected platform-interaction flag.
   * Backend defaults to `resident_studying` if absent on the wire, so
   * pre-persona profile rows back-compat-deserialise transparently.
   * UI surface for selection lands with the tourist-persona ticket
   * (ROADS-443) — this PR only plumbs the type through the contract.
   */
  persona: Persona;
  onboarding_completed: boolean;
  created_at_ms: number;
  updated_at_ms: number;
}

/**
 * Write-side payload. Omits the server-derived fields (`user_id`,
 * timestamps, `onboarding_completed`). The `PUT /api/profile` handler
 * merges these onto the existing row and echoes the full shape back.
 *
 * `persona` is optional on the write side — the backend defaults to
 * `resident_studying` if omitted, matching the back-compat invariant
 * documented on the Kotlin `ProfileUpdate` type.
 */
export interface UserProfileUpdate {
  home_country: string;
  native_language: LangCode;
  target_states: UsStateCode[];
  current_focus_state: UsStateCode | null;
  cdl_interest: boolean;
  persona?: Persona;
}

/**
 * Per-field validation feedback emitted by the backend (Wave 14e/3,
 * ROADS-339). Mirrors the Kotlin `FieldError` data class. The
 * server returns `{error:"validation_failed", fields:[{field,message},…]}`
 * for /api/profile validation failures so the wizard can highlight the
 * offending input rather than show a single banner.
 */
export interface ProfileFieldError {
  field: string;
  message: string;
}

/**
 * Wire shape of every /api/profile error body. Mixes the legacy
 * single-field hints (`field`, `message`) and the new `fields` array so
 * old + new clients each see what they expect.
 */
export interface ProfileErrorBody {
  error: string;
  field?: string;
  message?: string;
  fields?: ProfileFieldError[];
}

/**
 * Pull a normalised list of `ProfileFieldError` out of an arbitrary
 * `ApiError.body`. Works for:
 *   - new `{error:"validation_failed", fields:[…]}` shape;
 *   - legacy `{error:"invalid_profile", field, message}` shape (single
 *     entry constructed from the top-level field/message pair).
 *
 * Returns an empty array if the body doesn't carry any usable validation
 * hints — callers can then fall back to the bare `ApiError.message` as
 * before.
 */
export function extractProfileFieldErrors(
  body: unknown,
): ProfileFieldError[] {
  if (typeof body !== "object" || body === null) return [];
  const b = body as Partial<ProfileErrorBody>;
  if (Array.isArray(b.fields) && b.fields.length > 0) {
    return b.fields
      .filter(
        (f): f is ProfileFieldError =>
          typeof f === "object" &&
          f !== null &&
          typeof (f as ProfileFieldError).field === "string" &&
          typeof (f as ProfileFieldError).message === "string",
      )
      .map((f) => ({ field: f.field, message: f.message }));
  }
  if (typeof b.field === "string" && typeof b.message === "string") {
    return [{ field: b.field, message: b.message }];
  }
  return [];
}

/**
 * Wire-shape of `GET /api/profile/onboarding-status` — mirrors the Kotlin
 * `OnboardingStatusPayload`. `next_step` is a stringly-typed enum the
 * wizard maps to an index so it can fast-forward on mount.
 */
export interface OnboardingStatus {
  hasProfile: boolean;
  onboardingCompleted: boolean;
  nextStep:
    | "home_country"
    | "native_language"
    | "target_states"
    | "current_focus_state"
    | "done";
}

/**
 * Fetch the caller's profile. Returns `null` when the backend reports 404
 * (`{status:"no_profile", onboardingRequired:true}`) — callers treat that
 * as "new user, run onboarding from the top".
 *
 * Throws `ApiError` on any other non-2xx. 401 means the session cookie is
 * stale; ProtectedRoute surfaces that by bouncing to /login on the parent
 * route.
 */
export async function getProfile(): Promise<UserProfile | null> {
  try {
    return await apiFetch<UserProfile>("/api/profile");
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

/**
 * Upsert the caller's profile. The server validates `home_country` against
 * its allow-list and rejects inconsistent `current_focus_state`/`target_states`
 * combinations with 400 (the server's `message` field propagates as the
 * `ApiError.message`, so callers can surface it directly to the user).
 */
export async function putProfile(
  update: UserProfileUpdate,
): Promise<UserProfile> {
  return await apiFetch<UserProfile>("/api/profile", {
    method: "PUT",
    body: JSON.stringify(update),
  });
}

/**
 * Lightweight probe for the first-login gate. Never 404s — the server
 * returns `hasProfile:false, nextStep:"home_country"` for new users.
 */
export async function getOnboardingStatus(): Promise<OnboardingStatus> {
  return await apiFetch<OnboardingStatus>("/api/profile/onboarding-status");
}
